import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, BadgeCheck, Building2, CheckCircle, Loader2, Unlink } from 'lucide-react';

export const SnapchatGhostIcon = ({ className = 'h-4 w-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2c-3.3 0-5.9 2.6-5.9 5.9v2.4c-.4.2-1-.1-1.4-.3-.5-.2-1 .1-1.1.5-.1.5.3.9.8 1.1.7.3 1.4.5 1.6 1-.7 1.8-2.2 3.1-4 3.6-.4.1-.6.5-.4.9.4.7 1.6.9 2.7 1.1.2.4.2 1 .5 1.2.4.2 1.2-.2 2.2-.1 1.1.1 2.1 1.7 5 1.7s3.9-1.6 5-1.7c1-.1 1.8.3 2.2.1.3-.2.3-.8.5-1.2 1.1-.2 2.3-.4 2.7-1.1.2-.4 0-.8-.4-.9-1.8-.5-3.3-1.8-4-3.6.2-.5.9-.7 1.6-1 .5-.2.9-.6.8-1.1-.1-.4-.6-.7-1.1-.5-.4.2-1 .5-1.4.3V7.9C17.9 4.6 15.3 2 12 2z" />
  </svg>
);

export const SnapchatAccountSection = ({ snap }) => {
  const { connection, accounts, selectedAccount, setSelectedAccount, connect, disconnect, isLoadingAccounts, publicProfiles, profileScopeNeeded, selectedProfileId, saveDefaultProfile } = snap;

  if (!connection?.connected) {
    return (
      <div className="space-y-2">
        <Button onClick={connect} className="w-full bg-[#FFFC00] hover:bg-[#f0ed00] text-black font-semibold" data-testid="connect-snapchat-btn">
          <SnapchatGhostIcon className="h-4 w-4 mr-2" />
          Connect Snapchat Ads Account
        </Button>
        {connection?.error === 'refresh_failed' && (
          <p className="text-xs text-amber-300 flex items-center gap-1" data-testid="snapchat-reauth-hint">
            <AlertTriangle className="h-3 w-3" /> Authorization expired — please reconnect.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="snapchat-account-section">
      <div className="flex items-center justify-between p-3 rounded-lg bg-green-500/10 border border-green-500/30">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[#FFFC00] flex items-center justify-center text-black">
            <SnapchatGhostIcon />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-400" />
              <span className="text-sm text-green-300 font-medium">Snapchat Connected</span>
            </div>
            <span className="text-xs text-white/60" data-testid="snapchat-connected-name">{connection.display_name || connection.email}</span>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={disconnect} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs" data-testid="snapchat-disconnect-btn">
          <Unlink className="h-3 w-3 mr-1" /> Disconnect
        </Button>
      </div>

      <div className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-2">
        <Label className="text-white/80 text-sm font-medium flex items-center gap-2">
          <Building2 className="h-4 w-4" /> Ad Account
          {isLoadingAccounts && <Loader2 className="h-3 w-3 animate-spin text-white/50" />}
        </Label>
        {accounts.length > 0 ? (
          <Select value={selectedAccount?.id || ''} onValueChange={(value) => setSelectedAccount(accounts.find((a) => a.id === value) || null)}>
            <SelectTrigger className="bg-white/5 border-white/20 text-white" data-testid="snapchat-account-select">
              <SelectValue placeholder="Select an ad account" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-white/20">
              {accounts.map((account) => (
                <SelectItem key={account.id} value={account.id} className="text-white hover:bg-white/10">
                  <div className="flex items-center gap-2">
                    <span>{account.name || 'Unnamed'}</span>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${account.status === 'ACTIVE' ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'}`}>{account.status}</span>
                    {account.test && <span className="px-1.5 py-0.5 rounded text-xs bg-blue-500/20 text-blue-300">TEST</span>}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : !isLoadingAccounts && (
          <div className="flex items-start gap-2 text-xs text-yellow-300" data-testid="snapchat-no-accounts">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>No ad accounts found. Create one in Snapchat Business Manager, then reconnect.</span>
          </div>
        )}
        {selectedAccount && (
          <p className="text-xs text-white/40" data-testid="snapchat-account-meta">
            {selectedAccount.organization_name} • Currency: {selectedAccount.currency} • Timezone: {selectedAccount.timezone}
          </p>
        )}
      </div>

      <div className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-2" data-testid="snapchat-profile-section">
        <Label className="text-white/80 text-sm font-medium flex items-center gap-2"><BadgeCheck className="h-4 w-4" /> Public Profile *</Label>
        {publicProfiles.length > 0 ? (
          <Select value={selectedProfileId || ''} onValueChange={(v) => saveDefaultProfile(v, publicProfiles.find((p) => p.id === v)?.name)}>
            <SelectTrigger className="bg-white/5 border-white/20 text-white" data-testid="snapchat-profile-select">
              <SelectValue placeholder="Select the Public Profile shown on your ads" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-white/20">
              {publicProfiles.map((p) => (
                <SelectItem key={p.id} value={p.id} className="text-white hover:bg-white/10">{p.name}{p.username ? ` (@${p.username})` : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <>
            {profileScopeNeeded && (
              <Button type="button" onClick={() => connect({ withProfileScope: true })} className="w-full bg-[#FFFC00] hover:bg-[#f0ed00] text-black font-semibold text-xs" data-testid="snapchat-reconnect-profile-scope-btn">
                <BadgeCheck className="h-4 w-4 mr-2" /> Load my Public Profiles (reconnect with profile access)
              </Button>
            )}
            <p className="text-xs text-white/50">
              Recommended: reconnect above to pick your profile from a list. Otherwise paste the Public Profile ID from{' '}
              <a href="https://business.snapchat.com/" target="_blank" rel="noopener noreferrer" className="underline text-yellow-200">Business Manager → Public Profiles</a>
              {' '}— open the profile and copy the ID at the end of the URL. This is <span className="text-amber-300">not</span> your Organization ID or Ad Account ID.
            </p>
            <Input
              value={selectedProfileId || ''}
              onChange={(e) => saveDefaultProfile(e.target.value)}
              placeholder="Paste your Public Profile ID (e.g. 4f3a9c2e-…)"
              className="bg-white/5 border-white/20 text-white placeholder:text-white/30 font-mono text-xs"
              data-testid="snapchat-profile-id-input"
            />
          </>
        )}
        {selectedProfileId && selectedProfileId.length >= 8 && (
          <p className="text-xs text-green-300 flex items-center gap-1" data-testid="snapchat-profile-saved"><CheckCircle className="h-3 w-3" /> Profile saved — it will be used for all your Snapchat ads</p>
        )}
      </div>
    </div>
  );
};
